import React, { useEffect, useMemo, useRef, useState } from "react";
import UAUCAllPanel from "./UAUCAllPanel/UAUCAllPanel";
import UAUCUnresolvedPanel from "./UAUCUnresolvedPanel/UAUCUnresolvedPanel";
import TableLoader from "../TableComponent/TableLoader/TableLoader";
import MeetingAllPanel from "./MeetingAllPanel";
import InductionAllPanel from "./InductionAllPanel";
import SpecificMeetingAllPanel from "./SpecificMeetingAllPanel";
import AdminUserAnalytics from "./AdminUserAnalytics";
import "./ProjectSiteSummary.css";

const DEFAULT_API_BASE = "https://jkumarapi.axiomos.in";
const DEFAULT_PROJECTS_ENDPOINT = "/projects/summary/all";

const CLICKABLE_MODULES = [
  "uauc",
  "uaucunresolved",
  "tbtmeeting",
  "specifictraining",
  "induction",
];

const MODULE_LABELS = {
  uauc: "Observations",
  uaucunresolved: "Unresolved Observations",
  tbtmeeting: "TBT Meetings",
  specifictraining: "Specific Training",
  induction: "Inductions",
};

async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(
      data?.message || data?.error || `Request failed (${res.status})`,
    );
  }

  return data;
}

function normalizeModuleKey(key) {
  if (key === "tbtMeeting") return "tbtmeeting";
  if (key === "specificMeeting") return "specifictraining";
  if (key === "inductionMeeting") return "induction";
  if (key === "uaucUnresolved") return "uaucunresolved";
  return String(key || "").toLowerCase();
}

function formatModuleLabel(key) {
  const normalized = normalizeModuleKey(key);

  return (
    MODULE_LABELS[normalized] ||
    String(key || "")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .trim()
  );
}

function getProjectId(project) {
  return project?._id || project?.id || project?.projectId || "";
}

function getCountsTotal(counts = {}) {
  return Object.values(counts).reduce((sum, value) => {
    const number = Number(value);
    return sum + (Number.isFinite(number) ? number : 0);
  }, 0);
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;

  return (
    <div className="pss-modal-root">
      <button
        type="button"
        aria-label="Close modal"
        className="pss-modal-backdrop"
        onClick={onClose}
      />

      <section className="pss-modal">
        <header className="pss-modal-header">
          <div className="pss-modal-title-wrap">
            <p className="pss-modal-eyebrow">Project Module</p>
            <h3 className="pss-modal-title">{title}</h3>
          </div>

          <button type="button" onClick={onClose} className="pss-modal-close">
            x
          </button>
        </header>

        <div className="pss-modal-body">{children}</div>
      </section>
    </div>
  );
}

function ProjectCard({ project, counts, onClick }) {
  const countEntries = Object.entries(counts || {});
  const visibleCounts = countEntries.slice(0, 6);
  const total = getCountsTotal(counts);

  return (
    <button
      type="button"
      onClick={() => onClick?.(project)}
      className="pss-project-card"
    >
      <div className="pss-project-card-top">
        <div className="pss-project-main">
          <h3 className="pss-project-name">
            {project?.projectName || "Untitled Project"}
          </h3>

          {project?.siteLocation && (
            <p className="pss-project-location">{project.siteLocation}</p>
          )}
        </div>

        <span className="pss-view-pill">View</span>
      </div>

      <div className="pss-total-box">
        <p className="pss-total-label">Total Records</p>
        <div className="pss-total-value">{total}</div>
      </div>

      <div className="pss-count-grid">
        {visibleCounts.map(([key, value]) => (
          <div key={key} className="pss-count-card">
            <p className="pss-count-label">{formatModuleLabel(key)}</p>
            <p className="pss-count-value">{value ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="pss-card-action">
        <span>Open site summary</span>
        <span className="pss-card-arrow">-&gt;</span>
      </div>
    </button>
  );
}

function ProjectMetaGrid({ project }) {
  const meta = [
    ["Project Code", project?.projectId],
    ["Location", project?.siteLocation],
    ["Status", project?.status],
  ].filter(([, value]) => value);

  if (!meta.length) return null;

  return (
    <div className="pss-meta-grid">
      {meta.map(([label, value]) => (
        <div key={label} className="pss-meta-card">
          <p className="pss-meta-label">{label}</p>
          <p className="pss-meta-value">{value}</p>
        </div>
      ))}
    </div>
  );
}

function ModuleCard({ moduleKey, value, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`pss-module-card ${disabled ? "pss-module-card--disabled" : ""}`}
    >
      <div className="pss-module-top">
        <div>
          <p className="pss-module-label">{formatModuleLabel(moduleKey)}</p>
          <p className="pss-module-value">{value ?? 0}</p>
        </div>

        {!disabled && <span className="pss-module-pill">Open</span>}
      </div>

      <p className="pss-module-help">
        {disabled ? "Summary only" : "Click to inspect records"}
      </p>
    </button>
  );
}

export default function ProjectSiteSummary({
  apiBase = DEFAULT_API_BASE,
  projectsEndpoint = DEFAULT_PROJECTS_ENDPOINT,
  title = "Project Dashboard",
  subtitle = "Click a project to view site-wise totals for safety and compliance modules.",
}) {
  const [state, setState] = useState({ projects: [], loading: true, err: "" });
  const [query, setQuery] = useState("");
  const [activeProject, setActiveProject] = useState(null);
  const [activeEntity, setActiveEntity] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      try {
        setState((s) => ({ ...s, loading: true, err: "" }));

        const data = await safeFetchJSON(apiBase + projectsEndpoint, {
          signal: ac.signal,
        });

        setState({
          projects: Array.isArray(data) ? data : [],
          loading: false,
          err: "",
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          setState({
            projects: [],
            loading: false,
            err: error.message || "Failed to load projects.",
          });
        }
      }
    })();

    return () => ac.abort();
  }, [apiBase, projectsEndpoint]);

  const { projects, loading, err } = state;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;

    return projects.filter((item) => {
      const project = item?.project || {};
      return [
        project.projectName,
        project.siteLocation,
        project.projectId,
        project.company,
        project.status,
      ].some((field) =>
        String(field || "")
          .toLowerCase()
          .includes(q),
      );
    });
  }, [projects, query]);

  const activeProjectSummary = useMemo(() => {
    if (!activeProject) return null;
    const activeId = getProjectId(activeProject);
    return projects.find((item) => getProjectId(item?.project) === activeId);
  }, [activeProject, projects]);

  const normalized = String(activeEntity || "").toLowerCase();

  const summary = useMemo(() => {
    const totalProjects = projects.length;
    const totalRecords = projects.reduce(
      (sum, item) => sum + getCountsTotal(item?.counts),
      0,
    );

    return {
      totalProjects,
      visibleProjects: filtered.length,
      totalRecords,
    };
  }, [projects, filtered]);

  const modalTitle = activeProject
    ? `${activeProject.projectName || "Project"} - ${
        normalized
          ? MODULE_LABELS[normalized] || "Site Summary"
          : "Site Summary"
      }`
    : "";

  return (
    <main className="pss-page">
      <section className="pss-hero">
        <div className="pss-hero-inner">
          <div className="pss-hero-top">
            <div className="pss-hero-content">
              <p className="pss-eyebrow">Safety Operations</p>
              <h1 className="pss-title">{title}</h1>
              {subtitle && <p className="pss-subtitle">{subtitle}</p>}
            </div>

            <div className="pss-search-wrap">
              <label className="pss-search-label">Search Projects</label>
              <div className="pss-search-box">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, code, company, city..."
                  className="pss-search-input"
                />
                <span className="pss-search-icon">Search</span>
              </div>
            </div>
          </div>

          <div className="pss-summary-grid">
            <div className="pss-summary-card">
              <p className="pss-summary-label">All Projects</p>
              <p className="pss-summary-value">{summary.totalProjects}</p>
            </div>

            <div className="pss-summary-card">
              <p className="pss-summary-label">Projects in View</p>
              <p className="pss-summary-value">{summary.visibleProjects}</p>
            </div>

            <div className="pss-summary-card">
              <p className="pss-summary-label">Total Records</p>
              <p className="pss-summary-value">{summary.totalRecords}</p>
            </div>
          </div>
        </div>
      </section>

      {/* <div className="pss-analytics">
        <AdminUserAnalytics />
      </div> */}

      {loading ? (
        <div className="pss-loader-card">
          <TableLoader />
        </div>
      ) : err ? (
        <div className="pss-error-card">{err}</div>
      ) : filtered.length === 0 ? (
        <div className="pss-empty-card">
          <h3 className="pss-empty-title">No projects found</h3>
          <p className="pss-empty-text">
            Try a different project name, city, company, or project code.
          </p>
        </div>
      ) : (
        <section className="pss-project-grid">
          {filtered.map((item) => (
            <ProjectCard
              key={getProjectId(item?.project)}
              project={item?.project}
              counts={item?.counts}
              onClick={() => {
                setActiveProject(item?.project);
                setActiveEntity(null);
              }}
            />
          ))}
        </section>
      )}

      <div className="pss-analytics">
        <AdminUserAnalytics />
      </div>

      <Modal
        open={!!activeProject}
        onClose={() => {
          setActiveProject(null);
          setActiveEntity(null);
        }}
        title={modalTitle}
      >
        {activeProject && (
          <>
            <ProjectMetaGrid project={activeProject} />

            {!activeEntity && (
              <div className="pss-module-section">
                <div className="pss-module-heading">
                  <h4 className="pss-module-title">Module Summary</h4>
                  <p className="pss-module-subtitle">
                    Select a module to inspect project-level records.
                  </p>
                </div>

                <div className="pss-module-grid">
                  {Object.entries(activeProjectSummary?.counts || {}).map(
                    ([key, value]) => {
                      const normalizedKey = normalizeModuleKey(key);
                      const clickable =
                        CLICKABLE_MODULES.includes(normalizedKey);

                      return (
                        <ModuleCard
                          key={key}
                          moduleKey={key}
                          value={value}
                          disabled={!clickable}
                          onClick={() =>
                            clickable && setActiveEntity(normalizedKey)
                          }
                        />
                      );
                    },
                  )}
                </div>
              </div>
            )}

            {activeEntity && (
              <button
                type="button"
                onClick={() => setActiveEntity(null)}
                className="pss-back-btn"
              >
                Back to module summary
              </button>
            )}

            {normalized === "tbtmeeting" && (
              <MeetingAllPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}

            {normalized === "specifictraining" && (
              <SpecificMeetingAllPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}

            {normalized === "induction" && (
              <InductionAllPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}

            {normalized === "uauc" && (
              <UAUCAllPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}

            {normalized === "uaucunresolved" && (
              <UAUCUnresolvedPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}
          </>
        )}
      </Modal>
    </main>
  );
}
